import React, {
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import isEqual from 'lodash.isequal';
import {
  Accordion,
  AccordionHandle,
  AccordionItemType,
  Chip,
  Inline,
  Text,
} from '@treatwell/ui';
import { CmsBrowsePage, CmsFilters } from 'js/model/cms/cms-browse-page';
import { NamedEntityOutput } from 'js/model/rainbow/NamedEntityOutput';
import { Aggregations } from 'js/model/rainbow/browse-page/Aggregations';
import { SearchOptions } from 'js/model/rainbow/browse-page/SearchOptions';
import { VenueListingSpecificationOutput } from 'js/model/rainbow/browse-page/VenueListingSpecificationOutput';
import { VenueListingCriteria } from 'js/model/rainbow/browse-page/VenueListingCriteria';
import { Context } from 'js/components/LocaleWrapper';
import {
  PRICE_RANGE_FILTER_EXPONENT,
  getPriceRangeLowerBound,
  getPriceRangeUpperBound,
  isPriceRangeFilterActive,
  isPriceRangeFilterAvailable,
} from '../Filters/price-range-filter';
import { buildSortOptions, isSortFilterActive } from '../Filters/sort-filter';
import { RadioGroupFilter } from '../FilterModal/RadioGroupFilter';
import { PriceRangeFilter } from '../FilterModal/PriceRangeFilter';
import { trackSearch } from '../tracking/trackSearch';
import { FilterType, isFilterWithOptionsGuard, useFilters } from './useFilters';
import {
  RatingFilter,
  isRatingFilterActive,
} from './RatingFilter/RatingFilter';
import { AmenityFilter } from './AmenityFilter/AmenityFilter';
import { ALL_BRANDS, BrandFilter, Brands } from './BrandFilter/BrandFilter';
import styles from '../FilterModal/Filters.module.css';

export type FiltersHandle = {
  applyFilters: () => void;
  clearFilters: () => void;
};

export const getAmenityIds = (amenities: NamedEntityOutput[]) =>
  amenities.map((amenity) => amenity.id);

const getProductBrandOptions = (
  aggregations: Aggregations,
  cmsFilters: CmsFilters
): Brands => {
  const availableBrands = aggregations.brands || [];
  const hasAllBrands = availableBrands.some(
    (brand) => `${brand.id}` === ALL_BRANDS
  );

  if (!hasAllBrands) {
    return [
      {
        id: ALL_BRANDS,
        name: cmsFilters.brands['all-brands'],
      },
      ...(availableBrands ?? []),
    ] as Brands;
  }

  return availableBrands as Brands;
};

export const getProductBrandIds = (
  productBrands: NamedEntityOutput[]
): (number | 'allBrands')[] => {
  const productBrandIds = productBrands.map((productBrand) => productBrand.id);

  return productBrandIds.length === 0 ? [ALL_BRANDS] : productBrandIds;
};

export const Filters = ({
  aggregations,
  searchOptions,
  specification,
  initallyOpenedCategory,
  ref,
}: {
  aggregations: Aggregations;
  searchOptions: SearchOptions;
  specification: VenueListingSpecificationOutput;
  initallyOpenedCategory?: FilterType;
  ref: React.ForwardedRef<FiltersHandle>;
}) => {
  const { pageData } = useContext(Context);
  const accordionRef = useRef<AccordionHandle>(null);
  const { filters, applyFilters } = useFilters(
    aggregations,
    searchOptions,
    specification
  );
  const priceRangeLowerBound = getPriceRangeLowerBound(
    searchOptions,
    specification
  );
  const priceRangeUpperBound = getPriceRangeUpperBound(
    searchOptions,
    specification
  );
  const [appliedFilters, setAppliedFilters] = useState<
    Required<
      Pick<
        VenueListingSpecificationOutput,
        'sort' | 'price' | 'amenities' | 'productBrands'
      >
    > &
      Pick<VenueListingSpecificationOutput, 'minReviewRating'>
  >({
    sort: specification.sort ?? 'recommended',
    minReviewRating: specification.minReviewRating,
    price: {
      min: priceRangeLowerBound,
      max: priceRangeUpperBound,
    },
    amenities: specification.amenities ?? [],
    productBrands: specification.productBrands ?? [],
  });
  const cmsFilters = (pageData.cms as CmsBrowsePage).page.browse.filters;
  const appliedAmenitiesCount = appliedFilters.amenities.length;
  const hasAppliedAmenities = appliedAmenitiesCount > 0;
  const productBrandOptions = getProductBrandOptions(aggregations, cmsFilters);
  const appliedProductBrandIds = getProductBrandIds(
    appliedFilters.productBrands
  );

  const onProductBrandChange = (productBrandId: number | 'allBrands') => {
    const productBrand = productBrandOptions.find(
      (productBrand) => productBrand.id === productBrandId
    );

    if (productBrand) {
      setAppliedFilters({
        ...appliedFilters,
        productBrands:
          productBrand.id !== ALL_BRANDS
            ? [{ id: productBrand.id, name: productBrand.name }]
            : [],
      });
    }
  };

  const items = filters
    .filter(isFilterWithOptionsGuard)
    .map<AccordionItemType>(({ id, title }) => {
      let filterContent: React.ReactNode = null;
      let isActive: boolean = false;
      let count: number = 0;

      if (id === 'sortBy') {
        const sortOptions = buildSortOptions(searchOptions, cmsFilters);

        filterContent = (
          <RadioGroupFilter
            name={id}
            selected={appliedFilters.sort}
            options={sortOptions}
            onSelect={(sort) => {
              setAppliedFilters({ ...appliedFilters, sort });
            }}
          />
        );

        isActive = isSortFilterActive(appliedFilters);
        count = isActive ? 1 : 0;
      }

      if (id === 'rating') {
        filterContent = (
          <RatingFilter
            id={id}
            checked={(rating) => rating === appliedFilters.minReviewRating}
            onChange={(rating) => {
              setAppliedFilters({
                ...appliedFilters,
                minReviewRating: rating,
              });
            }}
          />
        );

        isActive = isRatingFilterActive(appliedFilters);
        count = isActive ? 1 : 0;
      }

      if (id === 'price') {
        filterContent = (
          <PriceRangeFilter
            min={searchOptions.minServicePrice!}
            max={searchOptions.maxServicePrice!}
            lower={appliedFilters.price.min}
            upper={appliedFilters.price.max}
            exponent={PRICE_RANGE_FILTER_EXPONENT}
            cms={cmsFilters}
            onChange={(min, max) => {
              setAppliedFilters({
                ...appliedFilters,
                price: { min, max },
              });
            }}
          />
        );

        isActive = isPriceRangeFilterActive(searchOptions, appliedFilters);
        count = isActive ? 1 : 0;
      }

      if (id === 'amenities') {
        filterContent = (
          <AmenityFilter
            amenities={aggregations.amenities || []}
            selectedAmenities={appliedFilters.amenities}
            onChange={(selectedAmenities) => {
              setAppliedFilters({
                ...appliedFilters,
                amenities: selectedAmenities,
              });
            }}
          />
        );

        isActive = hasAppliedAmenities;
        count = appliedAmenitiesCount;
      }

      if (id === 'brands') {
        const brandsCount = appliedFilters.productBrands.length;

        filterContent = (
          <BrandFilter
            brands={productBrandOptions}
            selectedBrandIds={appliedProductBrandIds}
            onChange={onProductBrandChange}
          />
        );

        isActive = brandsCount > 0;
        count = brandsCount;
      }

      return {
        id,
        title: (
          <Inline space="sm">
            <Text type="mdHeader">{title}</Text>
            {isActive && (
              <Chip text={`${count ?? '1'}`} className={styles.chip} />
            )}
          </Inline>
        ),
        content: filterContent,
      };
    });

  useEffect(() => {
    if (initallyOpenedCategory) {
      accordionRef.current?.scrollToItem(initallyOpenedCategory);
    }
  }, [initallyOpenedCategory]);

  useImperativeHandle(ref, () => {
    const selectedProductBrandsIds = getProductBrandIds(
      specification.productBrands ?? []
    );

    const haveFiltersBeenUpdated = () =>
      [
        appliedFilters.sort !== specification.sort,
        appliedFilters.minReviewRating !== specification.minReviewRating,
        appliedFilters.price.min !== priceRangeLowerBound,
        appliedFilters.price.max !== priceRangeUpperBound,
        !isEqual(
          appliedFilters.amenities.toSorted(),
          (specification.amenities ?? []).toSorted()
        ),
        !isEqual(appliedProductBrandIds, selectedProductBrandsIds),
      ].some(Boolean);

    return {
      applyFilters: () => {
        if (!haveFiltersBeenUpdated()) {
          return;
        }

        const criteria: VenueListingCriteria = {
          sort: appliedFilters.sort,
          minReviewRating: appliedFilters.minReviewRating,
          amenityIds: hasAppliedAmenities
            ? appliedFilters.amenities.map(({ id }) => id)
            : undefined,
          productBrandIds:
            appliedProductBrandIds[0] !== ALL_BRANDS
              ? (appliedProductBrandIds as number[])
              : undefined,
        };

        if (isPriceRangeFilterAvailable(searchOptions)) {
          const price = appliedFilters.price;

          if (isPriceRangeFilterActive(searchOptions, { price })) {
            criteria.price = price;
          } else {
            criteria.price = undefined;
          }
        }

        applyFilters(criteria);
        trackSearch('filter_applied', pageData.channel.country.countryCode, {
          ...specification,
          ...appliedFilters,
        });
      },
      clearFilters: () => {
        setAppliedFilters({
          sort: 'recommended',
          minReviewRating: undefined,
          price: {
            min: getPriceRangeLowerBound(searchOptions, {}),
            max: getPriceRangeUpperBound(searchOptions, {}),
          },
          amenities: [],
          productBrands: [],
        });
      },
    };
  }, [
    appliedFilters,
    appliedProductBrandIds,
    applyFilters,
    hasAppliedAmenities,
    pageData.channel.country.countryCode,
    priceRangeLowerBound,
    priceRangeUpperBound,
    searchOptions,
    specification,
  ]);

  return (
    <Accordion
      items={items}
      expandedItemId={initallyOpenedCategory}
      ref={accordionRef}
    />
  );
};
